import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, API_BASE } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { ArrowLeft, Trash, CheckCircle, Warning, XCircle, CalendarBlank, FilePdf } from "@phosphor-icons/react";
import DevilsAdvocateBlock from "@/components/DevilsAdvocateBlock";
import { toast } from "sonner";

const STATUS_MAP = {
    pending: { cls: "bg-[var(--ink-2)] text-white border-[var(--ink-2)]", Icon: CalendarBlank, label: "Analizando…" },
    safe: { cls: "bg-[var(--sage)] text-white border-[var(--sage)]", Icon: CheckCircle, label: "Seguro" },
    caution: { cls: "bg-[var(--amber)] text-white border-[var(--amber)]", Icon: Warning, label: "Precaución" },
    unsafe: { cls: "bg-[var(--terra-2)] text-white border-[var(--terra-2)]", Icon: XCircle, label: "Desaconsejado" },
};

const TIMES_ORDER = ["mañana", "mediodia", "tarde", "noche"];

export default function ScheduleDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { token } = useAuth();
    const [s, setS] = useState(null);

    const load = () => api.get(`/schedules/${id}`).then((r) => setS(r.data)).catch(() => setS(null));

    useEffect(() => {
        load();
    }, [id]);

    // Poll while safety is pending (max ~90s)
    useEffect(() => {
        if (s?.safety?.status !== "pending") return;
        let tries = 0;
        const t = setInterval(() => {
            tries += 1;
            load();
            if (tries > 18) clearInterval(t);  // ~90s
        }, 5000);
        return () => clearInterval(t);
        // eslint-disable-next-line
    }, [s?.safety?.status]);

    const del = async () => {
        if (!window.confirm("¿Eliminar cronograma?")) return;
        await api.delete(`/schedules/${id}`);
        toast.success("Cronograma eliminado");
        navigate("/cronogramas");
    };

    if (!s) return <div className="p-12"><span className="label-eyebrow">cargando…</span></div>;

    const st = STATUS_MAP[s.safety?.status] || STATUS_MAP.caution;
    const SI = st.Icon;
    const pdfUrl = `${API_BASE}/pdf/schedule/${s.id}?token=${encodeURIComponent(token() || "")}`;

    // Build a simple matrix: rows = formulas, cols = times of day
    const matrix = (s.items || []).map((it) => ({
        title: it.formula_title || "Fórmula",
        time: it.time_of_day,
        freq: it.frequency,
    }));

    return (
        <div className="fade-up">
            <PageHeader
                eyebrow={s.patient_snapshot?.name ? `Paciente: ${s.patient_snapshot.name}` : "Cronograma"}
                title={s.name}
                lead={s.safety?.summary}
                actions={
                    <div className="flex items-center gap-2 flex-wrap">
                        <a href={pdfUrl} target="_blank" rel="noreferrer" className="px-3.5 py-2 border hairline bg-white text-sm hover:bg-[var(--cream)] inline-flex items-center gap-2" data-testid="schedule-detail-pdf-button">
                            <FilePdf size={14}/> PDF
                        </a>
                        <button onClick={del} className="px-3.5 py-2 border hairline bg-white text-sm hover:bg-[var(--cream)] text-[var(--terra-2)] inline-flex items-center gap-2" data-testid="schedule-detail-delete-button"><Trash size={14}/> Eliminar</button>
                    </div>
                }
            >
                <div className="mt-4">
                    <Link to="/cronogramas" className="text-sm text-[var(--ink-2)] inline-flex items-center gap-1 hover:text-[var(--ink)]"><ArrowLeft size={14}/> Volver</Link>
                </div>
            </PageHeader>

            <div className="px-8 md:px-12 py-8 max-w-6xl grid lg:grid-cols-[2fr_1fr] gap-8">
                <div className="space-y-6">
                    <section className="border hairline bg-white p-5" data-testid="schedule-status">
                        <div className="flex items-center gap-3">
                            <span className={`citation-badge ${st.cls}`}><SI size={12}/> {st.label}</span>
                            <span className="text-sm">{s.safety?.summary || "Sin análisis"}</span>
                        </div>
                    </section>

                    <section className="border hairline bg-white p-5" data-testid="schedule-matrix">
                        <div className="label-eyebrow mb-4 flex items-center gap-2"><CalendarBlank size={14}/> Calendario diario</div>
                        <table className="data-table w-full">
                            <thead><tr><th>Fórmula</th>{TIMES_ORDER.map((t) => <th key={t} className="text-center capitalize">{t}</th>)}<th>Frec.</th></tr></thead>
                            <tbody>
                                {matrix.map((it, i) => (
                                    <tr key={i}>
                                        <td className="font-medium">{it.title}</td>
                                        {TIMES_ORDER.map((t) => (
                                            <td key={t} className="text-center">
                                                {it.time === t ? <span className="inline-block w-3 h-3 rounded-full bg-[var(--sage)]"></span> : <span className="text-[var(--ink-3)]">—</span>}
                                            </td>
                                        ))}
                                        <td className="text-xs mono">{it.freq.replace("_"," ")}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </section>

                    {s.safety?.cumulative_warnings?.length > 0 && (
                        <Block title="Dosis acumuladas">
                            <ul className="text-sm space-y-1.5">
                                {s.safety.cumulative_warnings.map((w, i) => (
                                    <li key={i} className={w.exceeds ? "text-[var(--terra-2)]" : ""}>
                                        • <strong>{w.compound}</strong>: {w.total_mg} mg {w.ul_mg ? `(UL: ${w.ul_mg} mg)` : ""} — {w.note}
                                    </li>
                                ))}
                            </ul>
                        </Block>
                    )}
                    {s.safety?.interactions?.length > 0 && <Block title="Interacciones">{s.safety.interactions.map((x, i) => <p key={i} className="text-sm mb-1">• {x}</p>)}</Block>}
                    {s.safety?.timing_recommendations?.length > 0 && <Block title="Timing">{s.safety.timing_recommendations.map((x, i) => <p key={i} className="text-sm mb-1">• {x}</p>)}</Block>}
                    {s.safety?.overall_recommendations?.length > 0 && <Block title="Recomendaciones">{s.safety.overall_recommendations.map((x, i) => <p key={i} className="text-sm mb-1">• {x}</p>)}</Block>}
                    <DevilsAdvocateBlock
                        devilsAdvocate={s.safety?.devils_advocate}
                        expectedComplications={s.safety?.expected_complications}
                        populationsWhereMightFail={s.safety?.populations_where_might_fail}
                        testid="schedule-devils-advocate"
                    />
                </div>

                <aside className="space-y-4">
                    <section className="border hairline bg-white p-5">
                        <div className="label-eyebrow mb-3">Fórmulas en el cronograma</div>
                        <div className="space-y-2">
                            {(s.items || []).map((it, i) => (
                                <Link key={i} to={`/formulas/${it.formula_id}`} className="block p-3 border hairline hover:bg-[var(--cream)]">
                                    <div className="text-sm font-medium">{it.formula_title || "Fórmula"}</div>
                                    <div className="text-xs text-[var(--ink-2)] mt-1">{it.time_of_day} · {it.frequency.replace("_"," ")}</div>
                                </Link>
                            ))}
                        </div>
                    </section>
                    {s.notes && (<section className="border hairline bg-white p-5"><div className="label-eyebrow mb-2">Notas</div><p className="text-sm">{s.notes}</p></section>)}
                </aside>
            </div>
        </div>
    );
}

function Block({ title, children }) {
    return (<section className="border hairline bg-white p-5"><div className="label-eyebrow mb-3">{title}</div>{children}</section>);
}
